import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchProduct } from "../../api/productClient";
import type { EmbedProductDto, EmbedProductResponse } from "../../types/tree";
import {
  ConfiguratorViewer,
  type ConfiguratorViewerHandle,
} from "../../components/ConfiguratorViewer";

export interface ProductPreviewToolProps {
  productId: string;
  /** Same-origin if omitted. */
  baseUrl?: string;
  /** Optional Unity target — when set, the viewer pane loads. */
  target?: string;
  stage?: "Development" | "Staging" | "Production";
  /**
   * API base URL the Unity build uses for its own asset/material fetches.
   * Defaults to the page's origin. Override for cross-origin embedding.
   */
  apiBaseUrl?: string;
}

// PascalCase to match Unity's MaterialBinding fields (JsonUtility is
// case-sensitive). Same shape as the spawn-bundle's defaultBindings.
interface SurfaceBinding {
  Key: string;
  DmatId: string;
}

/**
 * Translates picked material row ids into the DMatAsset ids Unity needs.
 * Unity's ProductLoader.FetchDMat hits /api/asset/material/{dmatId} which expects
 * the asset id (material.asset_id), not the material row id (material.id).
 */
function buildBindings(product: EmbedProductDto, materialPicks: Record<string, string>): SurfaceBinding[] {
  const out: SurfaceBinding[] = [];
  for (const s of product.surfaces) {
    const pickedMaterialId = materialPicks[s.surfaceKey] ?? s.defaultMaterialId;
    if (!pickedMaterialId) continue;
    const picked = s.materials.find(m => m.id === pickedMaterialId);
    if (!picked?.dmatAssetId) continue; // skip materials with no asset — Unity can't load them
    out.push({ Key: s.surfaceKey, DmatId: picked.dmatAssetId });
  }
  return out;
}

/**
 * Single-product preview. The ProductTreePreview tool without the tree: one fetch
 * loads the product + viewer, and the only interaction is picking materials. Useful
 * for previewing a product on its own instead of wrapping it in a one-node tree.
 *
 * Unity message contract is identical to the tree tool:
 *   - LoadProduct({ dmodelAssetId, bindings }) once the product loads
 *   - SetSurfaceMaterials({ bindings })        when material picks change
 */
export function ProductPreviewTool(props: ProductPreviewToolProps) {
  const [data, setData] = useState<EmbedProductResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [materialPicks, setMaterialPicks] = useState<Record<string, string>>({});
  const [unityError, setUnityError] = useState<string | null>(null);

  const viewerRef = useRef<ConfiguratorViewerHandle | null>(null);
  const [viewerReady, setViewerReady] = useState(false);

  // ── Fetch once on mount / when params change ────────────────────────
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    fetchProduct({
      baseUrl: props.baseUrl,
      productId: props.productId,
      target: props.target,
      stage: props.stage,
      signal: ctrl.signal,
    })
      .then(resp => {
        setData(resp);
        // Seed material picks from each surface's default.
        const seed: Record<string, string> = {};
        for (const s of resp.product.surfaces) {
          if (s.defaultMaterialId) seed[s.surfaceKey] = s.defaultMaterialId;
        }
        setMaterialPicks(seed);
      })
      .catch(err => {
        if (err.name === "AbortError") return;
        setError(err.message ?? String(err));
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [props.baseUrl, props.productId, props.target, props.stage]);

  const product = data?.product ?? null;

  // ── Push to Unity ───────────────────────────────────────────────────
  const lastSentProductIdRef = useRef<string | null>(null);

  // Send LoadProduct once the viewer is ready and the product is loaded.
  useEffect(() => {
    if (!viewerReady) return;
    const viewer = viewerRef.current;
    if (!viewer || !product) return;
    if (lastSentProductIdRef.current === product.id) return;

    const bindings = buildBindings(product, materialPicks);
    const payload = JSON.stringify({ dmodelAssetId: product.modelAssetId, bindings });
    setUnityError(null);
    lastSentProductIdRef.current = product.id;
    viewer.sendToUnity("LoadProduct", payload).catch(err =>
      console.warn("[ProductPreview] LoadProduct failed:", err)
    );
    // materialPicks intentionally excluded — LoadProduct carries the current picks;
    // later material-only changes go through the SetSurfaceMaterials effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerReady, product?.id]);

  // Send SetSurfaceMaterials when picks change for the already-loaded product.
  useEffect(() => {
    if (!viewerReady) return;
    const viewer = viewerRef.current;
    if (!viewer || !product) return;
    if (lastSentProductIdRef.current !== product.id) return;

    const bindings = buildBindings(product, materialPicks);
    viewer.sendToUnity("SetSurfaceMaterials", JSON.stringify({ bindings })).catch(err =>
      console.warn("[ProductPreview] SetSurfaceMaterials failed:", err)
    );
  }, [viewerReady, materialPicks, product]);

  const handlePickMaterial = useCallback((surfaceKey: string, materialId: string) => {
    setMaterialPicks(prev => ({ ...prev, [surfaceKey]: materialId }));
  }, []);

  const handleUnityEvent = useCallback((eventType: string, payload: string) => {
    if (eventType === "ProductLoaded") {
      setUnityError(null);
      return;
    }
    if (eventType === "LoadError") {
      try {
        const parsed = JSON.parse(payload) as { error?: string };
        setUnityError(parsed.error ?? payload);
      } catch {
        setUnityError(payload);
      }
    }
  }, []);

  if (loading && !data) {
    return <div className="dptp-state dptp-state--loading">Loading product…</div>;
  }
  if (error) {
    return <div className="dptp-state dptp-state--error">Error: {error}</div>;
  }
  if (!product) {
    return <div className="dptp-state dptp-state--empty">No data.</div>;
  }

  const pickableSurfaces = product.surfaces.filter(s => !s.locked);

  return (
    <div className="dptp">
      <div className="dptp__row">
        {/* Viewer column (left) */}
        <div className="dptp__col-viewer">
          <div className="dptp__viewer-card">
            {data?.viewer && product.modelAssetId ? (
              <ConfiguratorViewer
                ref={viewerRef}
                viewer={data.viewer}
                baseUrl={props.baseUrl}
                apiBaseUrl={props.apiBaseUrl}
                onReady={() => setViewerReady(true)}
                onUnityEvent={handleUnityEvent}
              />
            ) : product.imageUrl ? (
              <div className="dptp__viewer-image">
                <img src={product.imageUrl} alt={product.name} />
              </div>
            ) : (
              <div className="dptp__viewer-image">
                <div className="dptp__viewer-icon">📦</div>
              </div>
            )}
            {unityError && (
              <div className="dptp__viewer-error-banner">{unityError}</div>
            )}
          </div>
        </div>

        {/* Choices column (right) */}
        <div className="dptp__col-choices">
          <header className="dptp__header">
            <h2 className="dptp__title">{product.name}</h2>
          </header>

          {pickableSurfaces.length === 0 ? (
            <p className="dptp__muted">This product has no material choices.</p>
          ) : (
            pickableSurfaces.map(surface => {
              const current = materialPicks[surface.surfaceKey] ?? surface.defaultMaterialId;
              return (
                <section className="dptp__decision" key={surface.surfaceKey}>
                  <h6 className="dptp__decision-heading">{surface.surfaceLabel}</h6>
                  <div className="dptp__options">
                    {surface.materials.length === 0 && (
                      <span className="dptp__muted dptp__muted--small">
                        No materials in this collection.
                      </span>
                    )}
                    {surface.materials.map(m => {
                      const isSelected = current === m.id;
                      return (
                        <button
                          key={m.id}
                          type="button"
                          className={
                            "dptp__option" + (isSelected ? " dptp__option--selected" : "")
                          }
                          onClick={() => handlePickMaterial(surface.surfaceKey, m.id)}
                        >
                          {m.name}
                        </button>
                      );
                    })}
                  </div>
                </section>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductPreviewTool;
